import { useMemo } from "react";
import { string, z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ENTITY_TYPES_OPTIONS,
  INPUT_TYPES,
  VALID_FILE_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";

const initialData = {
  name: null,
  allowedFileFormats: [],
  dependentType: "",
};

export function DocumentCategoryModal({
  onModalClose,
  onSubmit,
  loading,
  open,
  editItem,
  documentCategories = [],
}) {
  const systemDefinedCategories = useMemo(() => {
    return documentCategories
      .filter((category) => category.isSystemDefined)
      .map((category) => ({
        value: category.dependentType,
        label: category.dependentType,
      }));
  }, [documentCategories]);

  const relatedToOptions = useMemo(() => {
    return [...ENTITY_TYPES_OPTIONS, ...systemDefinedCategories];
  }, [ENTITY_TYPES_OPTIONS, systemDefinedCategories]);

  const documentFields = [
    {
      label: "Document Category Name",
      name: "name",
      width: "100%",
      required: true,
      readOnly: editItem?.isSystemDefined,
      validationRule: z
        .string()
        .trim()
        .min(1, "Document Category name is required."),
    },
    {
      label: "Allowed File Formats",
      name: "allowedFileFormats",
      width: "100%",
      fieldType: INPUT_TYPES.MULTI_SELECT,
      options: VALID_FILE_OPTIONS,
      required: true,
      validationRule: z
        .array(string())
        .min(1, "Allowed File Formats is required."),
    },
    {
      label: "Related To",
      name: "dependentType",
      width: "100%",
      fieldType: INPUT_TYPES.SELECT,
      readOnly: editItem?.isSystemDefined,
      options: relatedToOptions,
      required: true,
      validationRule: z.string().min(1, "Related To is required"),
    },
  ];
  const edit = useEdit(editItem || initialData, documentFields);

  const handleModalClose = () => {
    onModalClose();
    edit.reset();
  };

  const handleSubmit = () => {
    onSubmit(edit.edits);
    handleModalClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleModalClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleModalClose,
        },
        {
          name: "Submit",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: handleSubmit,
          disabled: !edit.isDirty() || !edit.isValid(),
        },
      ]}
      loading={loading}
      title={`${editItem ? "Edit" : "Add"} Document Category`}
    >
      <MiDetailFields detailFields={documentFields} edit={edit} />
    </MiModal>
  );
}
